import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from './AuthContext';

export const LeagueContext = createContext();

export const LeagueProvider = ({ children }) => {
  const [activeLeague, setActiveLeagueState] = useState(null);
  const [isLeagueLoading, setIsLeagueLoading] = useState(false);
  const initializationRef = useRef(false);

  // Wrapper to ensure localStorage sync
  const setActiveLeague = (league) => {
    setActiveLeagueState(league);
    if (league) {
      localStorage.setItem('activeLeague', JSON.stringify(league));
    } else {
      localStorage.removeItem('activeLeague');
    }
  };

  // Clear league
  const clearActiveLeague = () => {
    setActiveLeague(null);
    initializationRef.current = false;
  };

  // Auto-initialize activeLeague when user data becomes available
  const { user, isAuthLoading } = useContext(AuthContext);

  useEffect(() => {
    // Don't initialize while auth is loading
    if (isAuthLoading) {
      return;
    }

    // Don't initialize if no user
    if (!user) {
      if (initializationRef.current) {
        clearActiveLeague();
      }
      return;
    }

    const leagueDetails = user.league_details;

    // If user has no leagues, clear active league
    if (!leagueDetails || leagueDetails.length === 0) {
      if (!initializationRef.current || activeLeague !== null) {
        setActiveLeague(null);
        initializationRef.current = true;
      }
      return;
    }

    // Check if we have a stored league
    const stored = localStorage.getItem('activeLeague');

    if (stored && !initializationRef.current) {
      try {
        const parsedLeague = JSON.parse(stored);

        // Find the fresh version of this league from the API response
        const freshLeague = leagueDetails.find(
          (league) => league.matchkeys?.league_id === parsedLeague.matchkeys?.league_id
        );

        if (freshLeague) {
          setActiveLeague(freshLeague);
          initializationRef.current = true;
          return;
        } else {
          localStorage.removeItem('activeLeague');
        }
      } catch (error) {
        console.error('Failed to restore stored league:', error);
        localStorage.removeItem('activeLeague');
      }
    }

    // Only auto-set first league if we haven't initialized yet and no active league
    if (!initializationRef.current && !activeLeague) {
      setActiveLeague(leagueDetails[0]);
      initializationRef.current = true;
    } else if (initializationRef.current && activeLeague) {
      // Update existing active league with fresh data
      const updatedLeague = leagueDetails.find(
        (league) => league.matchkeys?.league_id === activeLeague.matchkeys?.league_id
      );
      if (updatedLeague && JSON.stringify(updatedLeague) !== JSON.stringify(activeLeague)) {
        setActiveLeague(updatedLeague);
      }
    }
  }, [user, isAuthLoading]);

  return (
    <LeagueContext.Provider
      value={{
        activeLeague,
        setActiveLeague,
        isLeagueLoading,
        clearActiveLeague,
      }}
    >
      {children}
    </LeagueContext.Provider>
  );
};
